import React from "react";

import DotWithText from "./DotWithText";
import FullDentalChart from "./FullDentalChart";
import QuadrantSeparate from "./QuadrantSeparate";
import GlobalStyle from "../../../styles/GlobalStyle";
import { scaleVertical, FontSizes } from "../../../styles/responsive";
import { V2ReportStyle } from "../../../styles/V2ReportStyle";
import { PALLETTE } from "../../../utils/theme";
import { DmflabelMap } from "../../../utils/helpers/dentalHelper";
import { DentalIconSvg, DentalTeethIconSvg } from "../../../utils/Svgs";
import StatusLabel from "../../UI/StatusLabel";

const ExaminationOfTeeth = ({
  fullChartData,
  label = "Normal",
  suggestionText,
}: {
  fullChartData: any[];
  label: any;
  suggestionText: string;
}) => {
  const ailmentCounts: Record<string, number> = {};

  // Preserve exact logic for counting ailments
  for (const key in fullChartData) {
    const code = fullChartData[key];
    const label = DmflabelMap[code];

    if (label) {
      ailmentCounts[label] = (ailmentCounts[label] || 0) + 1;
    }
  }

  const ailmentKeys = Object.keys(ailmentCounts);
  const parts = Object.entries(ailmentCounts).map(
    ([key, count]) => `${count} ${key}`,
  );

  let conditionsText = "";
  let actionText = "Please tap on any one condition";

  if (parts.length === 0) {
    conditionsText = "no conditions";
    actionText = "";
  } else if (parts.length === 1) {
    conditionsText = parts[0];
    actionText = "Please tap on the condition below";
  } else {
    conditionsText = `${parts.slice(0, -1).join(", ")} and ${
      parts[parts.length - 1]
    }`;
    actionText = "Please tap on any one condition";
  }

  const message = `Hi! There are ${conditionsText} marked under the DMFT index. ${actionText}, and I’ll provide detailed information about it.`;

  return (
    <div>
      <div
        style={{
          ...V2ReportStyle.flexRowCenter,
          paddingBottom: scaleVertical(11),
          display: "flex", // Ensure layout consistency
        }}
      >
        <DentalIconSvg />
        <span
          style={{
            ...GlobalStyle.blackBoldText,
            fontSize: "2.2rem",
          }}
        >
          Examination of Teeth
        </span>
      </div>

      <div style={{ ...V2ReportStyle.card, gap: 16 }}>
        <div
          style={{
            ...V2ReportStyle.flexRowCenter,
            justifyContent: "space-between",
            gap: 5,
            display: "flex",
          }}
        >
          <div
            style={{ ...V2ReportStyle.flexRowCenter, gap: 5, display: "flex" }}
          >
            <DentalTeethIconSvg />
            <span
              style={{
                ...GlobalStyle.blackBoldText,
                fontSize: "2.2rem",
              }}
            >
              DMFT Index
            </span>
          </div>
          <StatusLabel lable={label} />
        </div>

        <div
          style={{ ...V2ReportStyle.flexRowCenter, gap: 10, display: "flex" }}
        >
          <DotWithText
            borderColor={PALLETTE.red}
            backgroundColor={PALLETTE.redShade1}
            title="DECAYED"
          />
          <DotWithText
            borderColor={PALLETTE.yellow}
            backgroundColor={PALLETTE.yellowShade1}
            title="MISSING"
          />
          <DotWithText
            borderColor={PALLETTE.blue}
            backgroundColor={PALLETTE.blueShade1}
            title="FILLED"
          />
        </div>

        {/* Chart Container - Logic for absolute positioning maintained */}
        <div
          style={{
            position: "relative",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: PALLETTE.white,
            display: "flex",
            flex: 1,
          }}
        >
          <FullDentalChart statusData={fullChartData} props={undefined} />

          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              // Web equivalent of transform: [{ translateX: -83 }, { translateY: -120 }]
              transform: "translate(-83px, -120px)",
              zIndex: 0,
            }}
          >
            <QuadrantSeparate />
          </div>
        </div>

        {suggestionText !== "" && (
          <div style={{ ...V2ReportStyle.flexRowCenter, display: "flex" }}>
            <span
              style={{
                ...GlobalStyle.blackMediumText,
                fontSize: "2.2rem",
              }}
            >
              {suggestionText}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExaminationOfTeeth;
